import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import {
  CountryDetailsFragment,
  CountryDetailsFragment_defaultCurrency,
  CountryDetailsFragment_defaultLanguage,
  CountryDetailsFragment_regions
} from "@saleor/fragments/types/CountryDetailsFragment";
import { CountryErrorFragment } from "@saleor/fragments/types/CountryErrorFragment";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchCurrencyrates_search_edges_node } from "@saleor/searches/types/SearchCurrencyrates";
import { SearchLanguages_search_edges_node } from "@saleor/searches/types/SearchLanguages";
import { FetchMoreProps, ReorderAction } from "@saleor/types";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import { useIntl } from "react-intl";

import CountryDetails from "../CountryDetails";
import Regions from "../Regions";

export interface CountryPageProps {
  country: CountryDetailsFragment | null;
  disabled: boolean;
  errors: CountryErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  regions: CountryDetailsFragment_regions[];
  onBack: () => void;
  onDelete: () => void;
  onSubmit: (data: CountryPageFormData) => void;
  onValueAdd: () => void;
  onValueDelete: (id: string) => void;
  onValueReorder: ReorderAction;
  onValueUpdate: (id: string) => void;
  languages: SearchLanguages_search_edges_node[];
  fetchLanguages: (data: string) => void;
  fetchMoreLanguages: FetchMoreProps;
  currencies: SearchCurrencyrates_search_edges_node[];
  fetchCurrencyrates: (data: string) => void;
  fetchMoreCurrencyrates: FetchMoreProps;
}

export interface CountryPageFormData {
  isoTwoCode: string;
  isoThreeCode: string;
  isoNumberCode: string;
  name: string;
  displayOrder: number;
  enable: boolean;
  defaultCurrency: CountryDetailsFragment_defaultCurrency;
  defaultLanguage: CountryDetailsFragment_defaultLanguage;
}

const CountryPage: React.FC<CountryPageProps> = ({
  country,
  disabled,
  errors,
  saveButtonBarState,
  regions,
  onBack,
  onDelete,
  onSubmit,
  onValueAdd,
  onValueDelete,
  onValueReorder,
  onValueUpdate,
  languages: languageChoiceList,
  currencies: currencyChoiceList,
  fetchLanguages,
  fetchMoreLanguages,
  fetchCurrencyrates,
  fetchMoreCurrencyrates
}) => {
  const intl = useIntl();
  const [defaultCurrency, setDefaultCurrency] = useStateFromProps(
    country?.defaultCurrency?.name || ""
  );
  const [defaultLanguage, setDefaultLanguage] = useStateFromProps(
    country?.defaultLanguage?.language || ""
  );
  const languages = maybe(() => languageChoiceList, []).map(language => ({
    label: language.language,
    value: language
  }));
  const currencyrates = maybe(() => currencyChoiceList, []).map(currency => ({
    label: currency.name,
    value: currency
  }));

  const initialForm: CountryPageFormData =
    country === null
      ? {
          isoTwoCode: "",
          isoThreeCode: "",
          isoNumberCode: "",
          name: "",
          displayOrder: 999,
          enable: true,
          defaultLanguage: null,
          defaultCurrency: null
        }
      : {
          isoTwoCode: maybe(() => country.isoTwoCode, ""),
          isoThreeCode: maybe(() => country.isoThreeCode, ""),
          isoNumberCode: maybe(() => country.isoNumberCode, ""),
          name: maybe(() => country.name, ""),
          displayOrder: maybe(() => country.displayOrder, 999),
          enable: maybe(() => country.enable, true),
          defaultLanguage: maybe(() => country.defaultLanguage, null),
          defaultCurrency: maybe(() => country.defaultCurrency, null)
        };

  const handleSubmit = (data: CountryPageFormData) =>
    onSubmit({
      ...data
    });

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => {
        const handleLanguageSelect = createSingleAutocompleteSelectHandler(
          change,
          setDefaultLanguage,
          languages
        );
        const handleCurrencySelect = createSingleAutocompleteSelectHandler(
          change,
          setDefaultCurrency,
          currencyrates
        );

        return (
          <Container>
            <AppHeader onBack={onBack}>
              {intl.formatMessage(sectionNames.countries)}
            </AppHeader>
            <PageHeader
              title={
                country === null
                  ? intl.formatMessage({
                      defaultMessage: "Create New Country",
                      description: "page title"
                    })
                  : maybe(() => country.name)
              }
            />
            <Grid>
              <div>
                <CountryDetails
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  onChange={change}
                  defaultCurrency={defaultCurrency}
                  defaultLanguage={defaultLanguage}
                  languages={languages}
                  onLanguageChange={handleLanguageSelect}
                  currencyrates={currencyrates}
                  onCurrencyChange={handleCurrencySelect}
                  fetchLanguages={fetchLanguages}
                  fetchMoreLanguages={fetchMoreLanguages}
                  fetchCurrencyrates={fetchCurrencyrates}
                  fetchMoreCurrencyrates={fetchMoreCurrencyrates}
                />
                <CardSpacer />
                <Regions
                  disabled={disabled}
                  regions={regions}
                  onValueAdd={onValueAdd}
                  onValueDelete={onValueDelete}
                  onValueReorder={onValueReorder}
                  onValueUpdate={onValueUpdate}
                />
                <CardSpacer />
              </div>
            </Grid>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              state={saveButtonBarState}
              onCancel={onBack}
              onSave={submit}
              onDelete={country === null ? undefined : onDelete}
            />
          </Container>
        );
      }}
    </Form>
  );
};
export default CountryPage;
